import { Bundle, Resource } from "@medplum/fhirtypes";
import axios from "axios";
import dayjs from "dayjs";
import { useState } from "react";
import { EhrDiff } from "../../../domain/ehr";
import { capture } from "../../../shared/capture";
import { swallowTimeoutOrNetworkError, swallowBadRequestError } from "../../error/utils";
import { useInterval } from "./use-interval";
import useMetriportToast from "./useMetriportToast";

type UseGetEhrDiff = {
  isFetchingDiff: boolean;
  metriportOnlyBundle: Bundle<Resource> | undefined;
  metriportOnlyResourceTypes: string[] | undefined;
  getMetriportOnlyBundleJob: () => Promise<void>;
};

const POLLING_INTERVAL = dayjs.duration(1, "second").asMilliseconds();
const EXPLAIN_TOAST_DURATION = dayjs.duration(25, "second").asMilliseconds();

export function useGetEhrDiff({
  ehr,
  ehrPatientId,
  diff,
}: {
  ehr?: string;
  ehrPatientId?: string;
  diff?: EhrDiff;
}): UseGetEhrDiff {
  const toast = useMetriportToast();

  const [isFetchingDiff, setIsFetchingDiff] = useState(false);
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [metriportOnlyBundle, setMetriportOnlyBundle] = useState<Bundle<Resource> | undefined>(
    undefined
  );
  const [metriportOnlyResourceTypes, setMetriportOnlyResourceTypes] = useState<
    string[] | undefined
  >(undefined);

  async function getMetriportOnlyBundleJob() {
    try {
      if (!diff || !ehr || !ehrPatientId) return;
      const jobRequest = await diff.getResourceDiffJob(ehrPatientId);
      if (!jobRequest || jobRequest.metadata.status === "failed") {
        onStartMetriportOnlyBundleJob();
        return;
      } else if (jobRequest.response) {
        const bundle = await downloadBundlesAndMerge(jobRequest.response.preSignedUrls);
        setMetriportOnlyBundle(bundle);
        setMetriportOnlyResourceTypes(jobRequest.response.resourceTypes);
      } else {
        setIsFetchingDiff(true);
        setJobId(jobRequest.metadata.id);
      }
    } catch (error) {
      console.log("error", error);
      const msg = "Failed to get latest patient resource diff";
      capture.error(msg, {
        extra: { patient: ehrPatientId, context: `patient.ehr.get-metriport-only-bundle`, error },
      });
      toast.error({ title: msg });
    }
  }

  async function onStartMetriportOnlyBundleJob() {
    try {
      if (!diff || !ehr || !ehrPatientId) return;
      setIsFetchingDiff(true);
      const jobId = await diff.startResourceDiffJob(ehrPatientId);
      setJobId(jobId);
      toast.info({
        title:
          "Computing the patient's resource difference between Metriport and EHR data. This may take some time depending on how much data is available.",
        duration: EXPLAIN_TOAST_DURATION,
      });
    } catch (error) {
      const { handled } = swallowBadRequestError(error);
      const msg = "Failed to start patient resource difference query";
      if (!handled) {
        capture.error(msg, {
          extra: {
            patient: ehrPatientId,
            context: `patient.ehr.start-metriport-only-job`,
            error,
          },
        });
      }
      toast.error({ title: msg });
      setIsFetchingDiff(false);
      setJobId(undefined);
    }
  }

  useInterval(
    async () => {
      function reset() {
        setIsFetchingDiff(false);
        setJobId(undefined);
      }
      if (!jobId || !diff || !ehr || !ehrPatientId) {
        reset();
        return;
      }
      try {
        const jobRequest = await diff.getResourceDiffJob(ehrPatientId, jobId);
        if (!jobRequest) {
          reset();
          return;
        }
        if (jobRequest.response) {
          const bundle = await downloadBundlesAndMerge(jobRequest.response.preSignedUrls);
          setMetriportOnlyBundle(bundle);
          setMetriportOnlyResourceTypes(jobRequest.response.resourceTypes);
          toast.success({
            title: "The patient's resource difference between Metriport and EHR data is ready.",
            duration: EXPLAIN_TOAST_DURATION,
          });
          reset();
        } else if (jobRequest.metadata.status === "failed") {
          toast.error({ title: "Failed to get patient resource difference" });
          reset();
        }
      } catch (error) {
        const { handled } = swallowTimeoutOrNetworkError(error);
        if (!handled) {
          const msg = "Failed to get patient resource diff";
          capture.error(msg, {
            extra: {
              patient: ehrPatientId,
              context: `patient.ehr.get-metriport-only-bundle`,
              error,
            },
          });
          toast.error({ title: msg });
          reset();
        }
      }
    },
    isFetchingDiff ? POLLING_INTERVAL : null
  );

  return {
    isFetchingDiff,
    metriportOnlyBundle,
    metriportOnlyResourceTypes,
    getMetriportOnlyBundleJob,
  };
}

async function downloadBundlesAndMerge(preSignedUrls: string[]): Promise<Bundle<Resource>> {
  const bundles: PromiseSettledResult<Bundle<Resource>>[] = await Promise.allSettled(
    preSignedUrls.map(async fileUrl => {
      const response = await axios.get<Bundle<Resource>>(fileUrl);
      if (!response.data) throw new Error("No data returned from bundle");
      return response.data;
    })
  );
  const emptyBundle = {
    resourceType: "Bundle",
    entry: [],
  } as Bundle<Resource>;
  const bundle: Bundle<Resource> = bundles.reduce((acc, curr) => {
    if (curr.status === "fulfilled") {
      acc.entry?.push(...(curr.value.entry ?? []));
    }
    return acc;
  }, emptyBundle);
  return bundle;
}
